import * as React from 'react'

import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'

export interface IColumnConfig {
  dataKey: string
  label: string
  isFixed: boolean
  isHidden: boolean
}

interface IStoredColumnConfig {
  dataKey: string
  isFixed: boolean
  isHidden: boolean
}

interface IParams {
  initialColumns: Array<
    Pick<IColumnConfig, 'dataKey' | 'label' | 'isFixed'> &
      Partial<Pick<IColumnConfig, 'isHidden'>>
  >
  projectId?: string
  userId?: string
  storageKey: LocalStorageKey
}

function normalizeColumns(columns: IColumnConfig[]): IColumnConfig[] {
  return [
    ...columns.filter(column => column.isFixed && !column.isHidden),
    ...columns.filter(column => !column.isFixed && !column.isHidden),
    ...columns.filter(column => column.isHidden),
  ]
}

function areSameColumns(a: IColumnConfig[], b: IColumnConfig[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (column, i) =>
        column.dataKey === b[i].dataKey &&
        column.label === b[i].label &&
        !!column.isFixed === !!b[i].isFixed &&
        !!column.isHidden === !!b[i].isHidden,
    )
  )
}

export function useColumnConfig({
  initialColumns,
  projectId,
  userId,
  storageKey,
}: IParams) {
  const [configuredColumns, setConfiguredColumns] = React.useState<
    IColumnConfig[]
  >([])
  const defaultColumns = React.useMemo(
    () =>
      normalizeColumns(
        initialColumns.map(column => ({
          ...column,
          isFixed: !!column.isFixed,
          isHidden: !!column.isHidden,
        })),
      ),
    [initialColumns],
  )

  const loadStoredConfig = React.useCallback(():
    | IStoredColumnConfig[]
    | null => {
    if (!userId || !projectId) return null

    try {
      const userPrefs = JSON.parse(localStorage.getItem(userId) || '{}')
      const columnConfig = userPrefs[storageKey]
      return columnConfig?.[projectId] || null
    } catch {
      return null
    }
  }, [projectId, storageKey, userId])

  const saveConfig = React.useCallback(
    (columns: IColumnConfig[]) => {
      if (!userId || !projectId) return

      try {
        const userPrefs = JSON.parse(localStorage.getItem(userId) || '{}')
        const columnConfig = userPrefs[storageKey] || {}

        columnConfig[projectId] = columns.map(column => ({
          dataKey: column.dataKey,
          isFixed: !!column.isFixed,
          isHidden: !!column.isHidden,
        }))

        userPrefs[storageKey] = columnConfig
        localStorage.setItem(userId, JSON.stringify(userPrefs))
      } catch {
        return
      }
    },
    [projectId, storageKey, userId],
  )

  const clearStoredConfig = React.useCallback(() => {
    if (!userId || !projectId) return

    try {
      const userPrefs = JSON.parse(localStorage.getItem(userId) || '{}')
      const columnConfig = userPrefs[storageKey]
      if (!columnConfig || !(projectId in columnConfig)) return

      delete columnConfig[projectId]
      userPrefs[storageKey] = columnConfig
      localStorage.setItem(userId, JSON.stringify(userPrefs))
    } catch {
      return
    }
  }, [projectId, storageKey, userId])

  // Bail out when the recomputed config is structurally unchanged: consumers
  // may pass a fresh initialColumns array every render (MobX observer views),
  // and an unconditional set here would loop render -> effect -> render.
  const applyColumns = React.useCallback((next: IColumnConfig[]) => {
    setConfiguredColumns(prev => (areSameColumns(prev, next) ? prev : next))
  }, [])

  React.useEffect(() => {
    if (initialColumns.length === 0) return

    const storedConfig = loadStoredConfig()

    if (!storedConfig) {
      applyColumns(defaultColumns)
      return
    }

    const columnsByKey = new Map(
      initialColumns.map(column => [column.dataKey, column]),
    )
    const processedKeys = new Set<string>()
    const nextColumns: IColumnConfig[] = []

    storedConfig.forEach(columnConfig => {
      const column = columnsByKey.get(columnConfig.dataKey)
      if (!column) return

      nextColumns.push({
        ...column,
        isFixed: !!columnConfig.isFixed,
        isHidden: !!columnConfig.isHidden,
      })
      processedKeys.add(columnConfig.dataKey)
    })

    initialColumns.forEach(column => {
      if (processedKeys.has(column.dataKey)) return

      nextColumns.push({
        ...column,
        isFixed: !!column.isFixed,
        isHidden: !!column.isHidden,
      })
    })

    applyColumns(nextColumns)
  }, [applyColumns, defaultColumns, initialColumns, loadStoredConfig])

  const updateColumnsConfiguration = React.useCallback(
    (columns: Pick<IColumnConfig, 'dataKey' | 'isFixed' | 'isHidden'>[]) => {
      const currentByKey = new Map(
        configuredColumns.map(column => [column.dataKey, column]),
      )
      const next = normalizeColumns(
        columns
          .map(columnConfig => {
            const currentColumn = currentByKey.get(columnConfig.dataKey)
            if (!currentColumn) return null

            return {
              ...currentColumn,
              isFixed: !!columnConfig.isFixed,
              isHidden: !!columnConfig.isHidden,
            }
          })
          .filter((column): column is IColumnConfig => column !== null),
      )

      setConfiguredColumns(next)
      saveConfig(next)
    },
    [configuredColumns, saveConfig],
  )

  const resetColumnsConfiguration = React.useCallback(() => {
    setConfiguredColumns(defaultColumns)
    clearStoredConfig()
  }, [clearStoredConfig, defaultColumns])

  return {
    configuredColumns,
    defaultColumns,
    updateColumnsConfiguration,
    resetColumnsConfiguration,
  }
}
